"use client";

import { useState } from "react";
import { TEMPLATES, templateToSettings, type TemplateId, type TemplatePreset } from "@/lib/templates";
import type { StorefrontSettings } from "@/types/product";

type Props = {
  onSelect: (settings: Partial<StorefrontSettings>, id: TemplateId, isRental: boolean) => void;
  onBack?: () => void;
  defaultSelectedId?: TemplateId | null;
};

// ─── Grocery ──────────────────────────────────────────────────────────────

const GROCERY_PRODUCE = ["#bbf7d0", "#fde68a", "#bfdbfe", "#fed7aa", "#fca5a5", "#ddd6fe", "#fbcfe8", "#bae6fd"] as const;
const GROCERY_PRICES = ["$1.49", "$2.49", "$1.99", "$3.49"] as const;
const GROCERY_CATS = ["Fruits &\nVeg", "Dairy &\nEggs", "Pantry", "Beverages", "Snacks", "Household", "Deals", "Organic"] as const;

function GroceryCardVisual() {
  return (
    <div style={{ overflow: "hidden", background: "#f5f3ee" }}>
      {/* Announcement bar */}
      <div style={{ background: "#14532d", padding: "2.5px 8px", color: "#fff", fontSize: 5.5, fontWeight: 500, textAlign: "center", letterSpacing: "0.03em" }}>
        Free delivery on orders over $35 · Same-day delivery available
      </div>
      {/* Header */}
      <div style={{ background: "#fff", padding: "5px 8px", display: "flex", alignItems: "center", gap: 5, borderBottom: "1px solid #e5e7eb" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: "#14532d", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="6" height="6" viewBox="0 0 16 16" fill="none" stroke="#fff" strokeWidth="2">
              <path d="M2 2h2l2 8h7l1-5H5" /><circle cx="9" cy="13" r="1.3" fill="#fff" stroke="none" /><circle cx="12" cy="13" r="1.3" fill="#fff" stroke="none" />
            </svg>
          </div>
          <div style={{ width: 22, height: 4, borderRadius: 1, background: "#14532d" }} />
        </div>
        <div style={{ flex: 1, height: 8, borderRadius: 4, background: "#f1f5f9", border: "1px solid #e2e8f0" }} />
        <div style={{ display: "flex", gap: 2 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#f1f5f9" }} />
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#f1f5f9" }} />
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#f1f5f9" }} />
        </div>
      </div>
      {/* Category nav (white bg) */}
      <div style={{ background: "#fff", padding: "3px 8px", borderBottom: "1px solid #e5e7eb", display: "flex", gap: 4, overflow: "hidden" }}>
        {["Departments ▾", "Fruits & Veg", "Dairy", "Bakery", "Pantry"].map((c, i) => (
          <div key={c} style={{ color: i === 0 ? "#14532d" : "#374151", fontSize: 5, fontWeight: i === 0 ? 700 : 400, whiteSpace: "nowrap", opacity: i > 3 ? 0.6 : 1 }}>{c}</div>
        ))}
      </div>
      {/* Main two-col layout */}
      <div style={{ display: "flex", gap: 5, padding: "6px 8px" }}>
        {/* Left */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 5 }}>
          {/* Hero card */}
          <div style={{ background: "#f5f0e8", borderRadius: 6, padding: "7px 8px", display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ flex: 1 }}>
              <div style={{ height: 6, width: "85%", borderRadius: 1, background: "#14532d", marginBottom: 3 }} />
              <div style={{ height: 5, width: "65%", borderRadius: 1, background: "#14532d", marginBottom: 4, opacity: 0.7 }} />
              <div style={{ height: 3, width: "78%", borderRadius: 1, background: "#64748b", opacity: 0.35, marginBottom: 5 }} />
              <div style={{ display: "flex", gap: 3 }}>
                <div style={{ height: 8, width: 26, borderRadius: 3, background: "#14532d" }} />
                <div style={{ height: 8, width: 26, borderRadius: 3, border: "1px solid #14532d" }} />
              </div>
            </div>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: "#14532d", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <div style={{ height: 2.5, width: 14, borderRadius: 1, background: "rgba(255,255,255,0.7)", marginBottom: 1 }} />
              <div style={{ fontSize: 6, fontWeight: 800, color: "#fff", lineHeight: 1 }}>$10</div>
              <div style={{ fontSize: 4, color: "rgba(255,255,255,0.8)" }}>OFF</div>
            </div>
          </div>
          {/* Category tiles */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 3 }}>
            {GROCERY_PRODUCE.slice(0, 4).map((color, i) => (
              <div key={i} style={{ background: "#fff", borderRadius: 4, border: "1px solid #e5e7eb", padding: "4px 2px", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                <div style={{ width: 16, height: 12, borderRadius: 3, background: color }} />
                <div style={{ height: 2.5, width: "70%", borderRadius: 1, background: "#374151", opacity: 0.25 }} />
              </div>
            ))}
          </div>
          {/* Products strip */}
          <div style={{ display: "flex", gap: 3, overflow: "hidden" }}>
            {GROCERY_PRODUCE.slice(0, 4).map((color, i) => (
              <div key={i} style={{ flex: 1, background: "#fff", borderRadius: 4, border: "1px solid #e5e7eb", overflow: "hidden" }}>
                <div style={{ height: 20, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ width: 14, height: 14, borderRadius: "50%", background: color }} />
                </div>
                <div style={{ padding: "2px 3px" }}>
                  <div style={{ height: 2.5, borderRadius: 1, background: "#111", opacity: 0.15, marginBottom: 1.5 }} />
                  <div style={{ height: 3, width: "60%", borderRadius: 1, background: "#14532d", fontWeight: 700 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Sidebar */}
        <div style={{ width: 36, display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
          {/* Order panel */}
          <div style={{ background: "#fff", borderRadius: 5, border: "1px solid #e5e7eb", padding: "4px" }}>
            <div style={{ height: 3.5, width: "65%", borderRadius: 1, background: "#14532d", marginBottom: 4 }} />
            <div style={{ height: 14, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#bbf7d0" }} />
              </div>
              <div style={{ height: 2, width: "80%", borderRadius: 1, background: "#374151", opacity: 0.15 }} />
            </div>
            <div style={{ height: 7, borderRadius: 3, background: "#14532d", marginTop: 3 }} />
          </div>
          {/* Hot Deals */}
          <div style={{ background: "#fff7ed", borderRadius: 5, border: "1px solid #fed7aa", padding: "4px" }}>
            <div style={{ height: 3, width: "70%", borderRadius: 1, background: "#c2410c", marginBottom: 3 }} />
            {[0, 1].map((i) => (
              <div key={i} style={{ display: "flex", gap: 2, marginBottom: 2 }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: GROCERY_PRODUCE[i], flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ height: 2, borderRadius: 1, background: "#374151", opacity: 0.2, marginBottom: 1.5 }} />
                  <div style={{ height: 2.5, width: "60%", borderRadius: 1, background: "#14532d" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function GroceryModalVisual({ onClose, onUse }: { onClose: () => void; onUse: () => void }) {
  return (
    <div style={{ background: "#f5f3ee" }}>
      {/* Announcement bar */}
      <div style={{ background: "#14532d", padding: "5px 0", color: "#fff", fontSize: 9, fontWeight: 500, textAlign: "center", letterSpacing: "0.03em" }}>
        🚚 Free delivery on orders over $35 &nbsp;|&nbsp; Same-day delivery available
      </div>
      {/* Header */}
      <div style={{ background: "#fff", height: 48, padding: "0 18px", display: "flex", alignItems: "center", gap: 12, borderBottom: "1px solid #e5e7eb" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, flexShrink: 0 }}>
          <div style={{ width: 28, height: 28, borderRadius: 6, background: "#14532d", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#fff" strokeWidth="1.8">
              <path d="M2 2h2l2 8h7l1-5H5" /><circle cx="9" cy="13" r="1.2" fill="#fff" stroke="none" /><circle cx="12" cy="13" r="1.2" fill="#fff" stroke="none" />
            </svg>
          </div>
          <div>
            <span style={{ fontSize: 13, fontWeight: 800, color: "#14532d", letterSpacing: "-0.01em" }}>Your Store</span>
            <div style={{ fontSize: 7, color: "#6b7280" }}>Good food. Real fresh.</div>
          </div>
        </div>
        <div style={{ flex: 1, height: 26, borderRadius: 20, background: "#f1f5f9", border: "1px solid #e2e8f0" }} />
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {["Account", "Lists", "Orders"].map((l) => (
            <div key={l} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#f1f5f9" }} />
              <div style={{ height: 3, width: 18, borderRadius: 1, background: "#9ca3af", opacity: 0.5 }} />
            </div>
          ))}
          <div style={{ width: 32, height: 26, borderRadius: 8, background: "#f9fafb", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", gap: 3 }}>
            <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#e5e7eb" }} />
            <div style={{ height: 3.5, width: 6, borderRadius: 1, background: "#14532d", fontWeight: 800 }} />
          </div>
        </div>
      </div>
      {/* Category nav — white bg, not green */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "0 18px", display: "flex", alignItems: "center", gap: 2, overflow: "hidden", height: 30 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 3, padding: "0 6px", height: "100%", borderBottom: "2px solid #14532d", color: "#14532d", fontSize: 8, fontWeight: 700, whiteSpace: "nowrap" }}>
          ☰ Departments ▾
        </div>
        {["🥬 Fresh Produce", "🥩 Meat & Seafood", "🧀 Deli", "🍞 Bakery", "🥫 Pantry", "🥤 Beverages", "❄️ Frozen"].map((c, i) => (
          <div key={c} style={{ color: "#374151", fontSize: 7.5, padding: "0 6px", height: "100%", display: "flex", alignItems: "center", whiteSpace: "nowrap", opacity: i > 4 ? 0.65 : 1 }}>{c}</div>
        ))}
      </div>

      {/* Main two-column layout */}
      <div style={{ display: "flex", gap: 12, padding: "12px 18px" }}>
        {/* Left content */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 10 }}>

          {/* Hero banner */}
          <div style={{ background: "#f5f0e8", borderRadius: 12, padding: "16px", display: "flex", alignItems: "center", gap: 12, position: "relative", overflow: "hidden" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 7.5, color: "#6b7280", marginBottom: 6 }}>Quality groceries, fast delivery or easy pickup.</div>
              <div style={{ fontSize: 15, fontWeight: 900, color: "#14532d", lineHeight: 1.15, marginBottom: 8 }}>Fresh food.<br />Made simple.</div>
              <div style={{ display: "flex", gap: 6 }}>
                <div style={{ padding: "5px 12px", background: "#14532d", color: "#fff", fontSize: 8, fontWeight: 700, borderRadius: 6 }}>Shop Groceries</div>
                <div style={{ padding: "5px 12px", border: "1px solid #14532d", color: "#14532d", fontSize: 8, fontWeight: 600, borderRadius: 6 }}>View Weekly Ad</div>
              </div>
              <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
                {[1,0,0,0].map((active, i) => (
                  <div key={i} style={{ height: 5, width: active ? 14 : 5, borderRadius: 3, background: active ? "#14532d" : "rgba(20,83,45,0.25)" }} />
                ))}
              </div>
            </div>
            <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#14532d", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <div style={{ fontSize: 6.5, color: "rgba(255,255,255,0.75)", textAlign: "center", lineHeight: 1.3 }}>New customers get</div>
              <div style={{ fontSize: 14, fontWeight: 900, color: "#fff", lineHeight: 1 }}>$10</div>
              <div style={{ fontSize: 8, fontWeight: 700, color: "#fff" }}>OFF</div>
              <div style={{ fontSize: 5.5, color: "rgba(255,255,255,0.7)", textAlign: "center" }}>your first order</div>
            </div>
          </div>

          {/* Shop by Category */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#14532d", marginBottom: 2 }}>Shop by Category</div>
            <div style={{ fontSize: 7, color: "#6b7280", marginBottom: 7 }}>Quick ideas for every occasion</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 5 }}>
              {GROCERY_PRODUCE.slice(0, 6).map((color, i) => (
                <div key={i} style={{ background: "#fff", borderRadius: 8, border: "1.5px solid #e5e7eb", padding: "6px 4px", display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                  <div style={{ width: "100%", height: 28, borderRadius: 5, background: color, opacity: 0.85 }} />
                  <div style={{ height: 4, width: "70%", borderRadius: 1, background: "#374151", opacity: 0.2 }} />
                </div>
              ))}
            </div>
          </div>

          {/* Popular Picks */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#14532d", marginBottom: 2 }}>Popular Picks</div>
            <div style={{ fontSize: 7, color: "#6b7280", marginBottom: 7 }}>See what everyone's buying</div>
            <div style={{ display: "flex", gap: 5, overflow: "hidden" }}>
              {GROCERY_PRODUCE.slice(0, 5).map((color, i) => (
                <div key={i} style={{ width: 70, flexShrink: 0, background: "#fff", borderRadius: 8, border: "1px solid #e5e7eb", overflow: "hidden" }}>
                  <div style={{ height: 42, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: color }} />
                  </div>
                  <div style={{ padding: "4px 5px 5px" }}>
                    <div style={{ height: 4, borderRadius: 1, background: "#111827", opacity: 0.15, marginBottom: 2 }} />
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 3 }}>
                      <div style={{ fontSize: 8, fontWeight: 700, color: "#14532d" }}>{GROCERY_PRICES[i % 4]}</div>
                      <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <span style={{ color: "#fff", fontSize: 9, fontWeight: 700, lineHeight: 1 }}>+</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Trust badges */}
          <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 8, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
            {["✓ Fresh\nGuarantee", "↩ Easy\nReturns", "🔒 Secure\nPayments", "🌱 Sustainable\nChoices"].map((badge) => (
              <div key={badge} style={{ fontSize: 6.5, color: "#374151", lineHeight: 1.4 }}>{badge}</div>
            ))}
          </div>
        </div>

        {/* Right sidebar */}
        <div style={{ width: 130, flexShrink: 0, display: "flex", flexDirection: "column", gap: 8 }}>
          {/* Your Order */}
          <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", overflow: "hidden" }}>
            <div style={{ padding: "8px 10px", borderBottom: "1px solid #f3f4f6" }}>
              <div style={{ fontSize: 9.5, fontWeight: 700, color: "#14532d" }}>Your Order</div>
            </div>
            <div style={{ padding: "10px", display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.5">
                  <path d="M3 3h2l.4 2M7 13h10l4-8H5.4m1.6 8a2 2 0 100 4 2 2 0 000-4zm10 0a2 2 0 100 4 2 2 0 000-4z" />
                </svg>
              </div>
              <div style={{ fontSize: 7.5, fontWeight: 600, color: "#14532d" }}>Your cart is empty</div>
              <div style={{ fontSize: 6.5, color: "#6b7280", textAlign: "center" }}>Shop our fresh selection</div>
              <div style={{ width: "100%", padding: "6px 0", background: "#14532d", color: "#fff", fontSize: 8, fontWeight: 700, textAlign: "center", borderRadius: 6 }}>Start Shopping</div>
            </div>
            {/* Pickup/Delivery */}
            <div style={{ borderTop: "1px solid #f3f4f6", padding: "7px 10px" }}>
              <div style={{ fontSize: 8, fontWeight: 600, color: "#14532d", marginBottom: 5 }}>Pick up or Delivery</div>
              <div style={{ display: "flex", gap: 4 }}>
                <div style={{ flex: 1, padding: "4px 0", border: "1px solid #16a34a", color: "#16a34a", fontSize: 7, fontWeight: 600, textAlign: "center", borderRadius: 5 }}>📍 Pickup</div>
                <div style={{ flex: 1, padding: "4px 0", border: "1px solid #e5e7eb", color: "#6b7280", fontSize: 7, textAlign: "center", borderRadius: 5 }}>🚚 Delivery</div>
              </div>
              <div style={{ fontSize: 6, color: "#6b7280", marginTop: 4 }}>Free delivery on orders over $35</div>
            </div>
          </div>

          {/* Hot Deals */}
          <div style={{ background: "#fff7ed", borderRadius: 10, border: "1px solid #fed7aa", overflow: "hidden" }}>
            <div style={{ padding: "7px 10px", borderBottom: "1px solid #ffedd5", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#c2410c" }}>Hot Deals</div>
              <div style={{ fontSize: 7, color: "#f97316" }}>View all</div>
            </div>
            {GROCERY_PRODUCE.slice(0, 2).map((color, i) => (
              <div key={i} style={{ padding: "7px 10px", borderBottom: i === 0 ? "1px solid #ffedd5" : "none", display: "flex", gap: 6, alignItems: "center" }}>
                <div style={{ width: 36, height: 36, borderRadius: 6, background: "#fff", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ width: 26, height: 26, borderRadius: "50%", background: color }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ height: 4, borderRadius: 1, background: "#374151", opacity: 0.18, marginBottom: 3 }} />
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#14532d" }}>{GROCERY_PRICES[i]}</div>
                    <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ color: "#fff", fontSize: 9, fontWeight: 700, lineHeight: 1 }}>+</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Membership promo */}
          <div style={{ background: "#fefce8", borderRadius: 10, border: "1px solid #fef08a", padding: "9px 10px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
              <span style={{ fontSize: 12 }}>🌟</span>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#854d0e" }}>Store Plus</div>
            </div>
            <div style={{ fontSize: 7, color: "#92400e", lineHeight: 1.4, marginBottom: 5 }}>Unlimited free delivery and extra savings all year long.</div>
            <div style={{ fontSize: 7.5, fontWeight: 700, color: "#854d0e" }}>Learn More →</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ height: 22, background: "#14532d" }} />
      {/* Actions */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "14px 16px", borderTop: "1px solid #e2e8f0", background: "#fff" }}>
        <button type="button" onClick={onClose} style={{ padding: "8px 18px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#f8fafc", fontSize: 14, fontWeight: 500, cursor: "pointer", color: "#475569" }}>Close</button>
        <button type="button" onClick={onUse} style={{ padding: "8px 18px", borderRadius: 10, border: "none", background: "#16a34a", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Use this template</button>
      </div>
    </div>
  );
}

function SportRetailCardVisual() {
  return (
    <div style={{ overflow: "hidden", background: "#fff" }}>
      {/* Announcement bar */}
      <div style={{ background: "#111", padding: "2.5px 8px", color: "#fff", fontSize: 5.5, fontWeight: 500, textAlign: "center", letterSpacing: "0.06em" }}>
        FREE STANDARD DELIVERY ON ORDERS $100+
      </div>
      {/* Header */}
      <div style={{ background: "#fff", padding: "6px 10px", display: "flex", alignItems: "center", gap: 6, borderBottom: "1px solid #e8e8e8" }}>
        <span style={{ fontSize: 10, fontWeight: 900, color: "#111", letterSpacing: "-0.03em", flexShrink: 0 }}>LOGO</span>
        <div style={{ flex: 1, display: "flex", justifyContent: "center", gap: 5 }}>
          {["Men", "Women", "Kids", "Sale"].map((c, i) => (
            <div key={c} style={{ fontSize: 4.5, fontWeight: 700, color: "#111", letterSpacing: "0.05em", opacity: i === 0 ? 1 : 0.45 }}>{c}</div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 3 }}>
          {[1, 2, 3].map((i) => <div key={i} style={{ width: 9, height: 9, borderRadius: "50%", background: "#f0f0f0" }} />)}
        </div>
      </div>
      {/* Hero */}
      <div style={{ background: "#111", padding: "10px 10px", display: "flex", alignItems: "center", gap: 8, minHeight: 52 }}>
        <div style={{ flex: 1 }}>
          <div style={{ height: 9, borderRadius: 1, background: "#fff", marginBottom: 3, width: "88%" }} />
          <div style={{ height: 9, borderRadius: 1, background: "#fff", marginBottom: 7, width: "68%" }} />
          <div style={{ display: "flex", gap: 3 }}>
            <div style={{ height: 11, width: 32, background: "#fff" }} />
            <div style={{ height: 11, width: 36, background: "transparent", border: "1px solid rgba(255,255,255,0.5)" }} />
          </div>
        </div>
        <div style={{ width: 36, height: 48, background: "#333", flexShrink: 0 }} />
      </div>
      {/* Category tiles 4-col */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1 }}>
        {(["#d8d8d8", "#e0e0e0", "#cccccc", "#e8e8e8"] as const).map((bg, i) => (
          <div key={i} style={{ background: bg, padding: "8px 4px", position: "relative", minHeight: 38 }}>
            <div style={{ position: "absolute", bottom: 4, left: 4 }}>
              <div style={{ height: 4, width: 16, borderRadius: 1, background: "#111", marginBottom: 2 }} />
              <div style={{ height: 3, width: 12, borderRadius: 1, background: "#333", opacity: 0.55 }} />
            </div>
          </div>
        ))}
      </div>
      {/* Best Sellers */}
      <div style={{ background: "#fff", padding: "6px 8px 8px" }}>
        <div style={{ height: 5, width: 52, borderRadius: 1, background: "#111", fontWeight: 900, marginBottom: 5 }} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4 }}>
          {[1, 2, 3].map((i) => (
            <div key={i} style={{ background: "#f5f5f5", borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: 28, background: "#ebebeb", position: "relative" }}>
                <div style={{ position: "absolute", top: 2, right: 2, width: 7, height: 7, borderRadius: "50%", background: "rgba(0,0,0,0.08)" }} />
              </div>
              <div style={{ padding: "3px 4px 4px" }}>
                <div style={{ height: 3, borderRadius: 1, background: "#111", opacity: 0.12, marginBottom: 2 }} />
                <div style={{ height: 3.5, width: "55%", borderRadius: 1, background: "#111", opacity: 0.5 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SportRetailModalVisual({ onClose, onUse }: { onClose: () => void; onUse: () => void }) {
  return (
    <div style={{ background: "#fff" }}>
      {/* Announcement bar */}
      <div style={{ background: "#111", padding: "5px 0", color: "#fff", fontSize: 9, fontWeight: 500, textAlign: "center", letterSpacing: "0.08em" }}>
        FREE STANDARD DELIVERY ON ORDERS $100+
      </div>
      {/* Header */}
      <div style={{ background: "#fff", height: 48, padding: "0 20px", display: "flex", alignItems: "center", gap: 14, borderBottom: "1px solid #e8e8e8" }}>
        <span style={{ fontSize: 16, fontWeight: 900, color: "#111", letterSpacing: "-0.03em", flexShrink: 0 }}>LOGO</span>
        <div style={{ flex: 1, display: "flex", justifyContent: "center", gap: 14, alignItems: "center" }}>
          {(["Men", "Women", "Kids", "New Arrivals", "Sale", "Collections"] as string[]).map((c) => (
            <span key={c} style={{ fontSize: 7.5, fontWeight: 700, color: "#111", letterSpacing: "0.05em" }}>{c}</span>
          ))}
        </div>
        <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
          {[1, 2, 3].map((i) => <div key={i} style={{ width: 18, height: 18, borderRadius: "50%", background: "#f0f0f0" }} />)}
        </div>
      </div>
      {/* Hero */}
      <div style={{ background: "#111", padding: "26px 24px", display: "flex", alignItems: "center", gap: 20 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 7, color: "rgba(255,255,255,0.45)", letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 9 }}>New Season</div>
          <div style={{ height: 22, borderRadius: 2, background: "#fff", marginBottom: 5, width: "92%" }} />
          <div style={{ height: 22, borderRadius: 2, background: "#fff", marginBottom: 14, width: "73%" }} />
          <div style={{ fontSize: 8, color: "rgba(255,255,255,0.5)", marginBottom: 16, lineHeight: 1.5 }}>Engineered for performance.<br />Designed for every move.</div>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ height: 28, width: 82, background: "#fff" }} />
            <div style={{ height: 28, width: 92, background: "transparent", border: "1.5px solid rgba(255,255,255,0.55)" }} />
          </div>
        </div>
        <div style={{ width: 140, height: 128, background: "#333", flexShrink: 0 }} />
      </div>
      {/* Category tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
        {(["#d0d0d0", "#dcdcdc", "#c8c8c8", "#e4e4e4"] as string[]).map((bg, i) => (
          <div key={i} style={{ background: bg, padding: "14px 10px", position: "relative", minHeight: 62 }}>
            <div style={{ position: "absolute", bottom: 8, left: 10 }}>
              <div style={{ fontSize: 9, fontWeight: 900, color: "#111", letterSpacing: "-0.01em", marginBottom: 2 }}>
                {(["MEN", "WOMEN", "KIDS", "NEW ARRIVALS"] as string[])[i]}
              </div>
              <div style={{ fontSize: 7, color: "#333" }}>Shop Now →</div>
            </div>
          </div>
        ))}
      </div>
      {/* Best Sellers */}
      <div style={{ background: "#fff", padding: "12px 18px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 900, color: "#111", letterSpacing: "-0.01em" }}>BEST SELLERS</div>
          <div style={{ fontSize: 7.5, color: "#111", fontWeight: 700 }}>View All →</div>
        </div>
        <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #e8e8e8", marginBottom: 10 }}>
          {(["All", "Shoes", "Tops", "Bottoms"] as string[]).map((tab, i) => (
            <div key={tab} style={{ padding: "4px 10px", fontSize: 7.5, fontWeight: 700, color: i === 0 ? "#111" : "#aaa", borderBottom: i === 0 ? "2px solid #111" : "2px solid transparent", marginBottom: -1 }}>{tab}</div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 6 }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} style={{ background: "#f5f5f5", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ height: 42, background: "#ebebeb", position: "relative" }}>
                <div style={{ position: "absolute", top: 3, right: 3, width: 9, height: 9, borderRadius: "50%", background: "rgba(0,0,0,0.07)" }} />
              </div>
              <div style={{ padding: "4px 5px 5px" }}>
                <div style={{ height: 4, borderRadius: 1, background: "#111", opacity: 0.1, marginBottom: 2 }} />
                <div style={{ height: 4, width: "55%", borderRadius: 1, background: "#111", opacity: 0.45 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Promo banners */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
        <div style={{ background: "#111", padding: "14px 16px", color: "#fff", minHeight: 72 }}>
          <div style={{ fontSize: 7, color: "rgba(255,255,255,0.45)", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 4 }}>Become a Member</div>
          <div style={{ fontSize: 11, fontWeight: 900, color: "#fff", marginBottom: 8, letterSpacing: "-0.01em" }}>GET REWARDED</div>
          <div style={{ height: 18, width: 54, background: "#fff" }} />
        </div>
        <div style={{ background: "#e0e0e0", padding: "14px 16px", color: "#111", minHeight: 72 }}>
          <div style={{ fontSize: 7, color: "rgba(0,0,0,0.4)", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 4 }}>New Collection</div>
          <div style={{ fontSize: 10, fontWeight: 900, color: "#111", marginBottom: 6, letterSpacing: "-0.01em", lineHeight: 1.2 }}>BUILT FOR<br />EVERY SEASON</div>
          <div style={{ fontSize: 7.5, color: "#333", fontWeight: 700 }}>SHOP COLLECTION →</div>
        </div>
      </div>
      {/* Trust badges */}
      <div style={{ padding: "8px 18px", display: "flex", justifyContent: "space-around", borderTop: "1px solid #e8e8e8" }}>
        {(["Free Delivery", "Free Returns", "10% Off", "Secure Pay"] as string[]).map((b) => (
          <div key={b} style={{ fontSize: 7.5, color: "#111", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#f0f0f0" }} />
            {b}
          </div>
        ))}
      </div>
      {/* Footer */}
      <div style={{ height: 24, background: "#111" }} />
      {/* Actions */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "14px 16px", borderTop: "1px solid #e2e8f0", background: "#fff" }}>
        <button type="button" onClick={onClose} style={{ padding: "8px 18px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#f8fafc", fontSize: 14, fontWeight: 500, cursor: "pointer", color: "#475569" }}>Close</button>
        <button type="button" onClick={onUse} style={{ padding: "8px 18px", borderRadius: 10, border: "none", background: "#111", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Use this template</button>
      </div>
    </div>
  );
}

// ─── Dispatch helpers ────────────────────────────────────────────────────

function CardVisual({ id }: { id: TemplateId }) {
  if (id === "grocery") return <GroceryCardVisual />;
  return <SportRetailCardVisual />;
}

function ModalVisual({ id, onClose, onUse }: { id: TemplateId; onClose: () => void; onUse: () => void }) {
  if (id === "grocery") return <GroceryModalVisual onClose={onClose} onUse={onUse} />;
  return <SportRetailModalVisual onClose={onClose} onUse={onUse} />;
}

// ─── Template card & modal shells ────────────────────────────────────────

function TemplateCard({
  template,
  selected,
  onSelect,
  onPreview,
}: {
  template: TemplatePreset;
  selected: boolean;
  onSelect: () => void;
  onPreview: () => void;
}) {
  return (
    <div
      onClick={onSelect}
      style={{
        borderRadius: 12,
        border: selected ? `2px solid ${template.primaryColor}` : "2px solid #e2e8f0",
        cursor: "pointer",
        overflow: "hidden",
        boxShadow: selected ? `0 0 0 3px ${template.primaryColor}33` : "none",
        transition: "box-shadow 0.15s, border-color 0.15s",
        background: "#fff",
      }}
    >
      <CardVisual id={template.id} />
      <div style={{
        padding: "10px 12px 12px",
        borderTop: "1px solid #e2e8f0",
        background: "#fff",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: "#0f172a" }}>
              {template.name}
            </p>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748b", lineHeight: 1.4 }}>
              {template.tagline}
            </p>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPreview(); }}
            style={{
              marginLeft: 8,
              flexShrink: 0,
              fontSize: 12,
              fontWeight: 500,
              color: template.primaryColor,
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              textDecoration: "underline",
            }}
          >
            Preview
          </button>
        </div>
      </div>
    </div>
  );
}

function TemplatePreviewModal({
  template,
  onClose,
  onUse,
}: {
  template: TemplatePreset;
  onClose: () => void;
  onUse: () => void;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.6)",
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          borderRadius: 16,
          overflow: "hidden",
          maxWidth: 640,
          width: "100%",
          boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalVisual id={template.id} onClose={onClose} onUse={onUse} />
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────

export function TemplatePicker({ onSelect, onBack, defaultSelectedId }: Props) {
  const [selectedId, setSelectedId] = useState<TemplateId | null>(defaultSelectedId ?? null);
  const [previewId, setPreviewId] = useState<TemplateId | null>(null);
  const [isRental, setIsRental] = useState(false);

  const previewTemplate = previewId ? TEMPLATES.find((t) => t.id === previewId) : null;

  const handleNext = () => {
    if (!selectedId) return;
    const chosen = TEMPLATES.find((t) => t.id === selectedId);
    if (!chosen) return;
    onSelect(templateToSettings(chosen, isRental), selectedId, isRental);
  };

  const btnPrimary: React.CSSProperties = {
    borderRadius: 10,
    background: "#2563eb",
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 600,
    color: "#fff",
    border: "none",
    cursor: selectedId ? "pointer" : "not-allowed",
    opacity: selectedId ? 1 : 0.5,
    transition: "opacity 0.15s",
  };

  const btnSecondary: React.CSSProperties = {
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 500,
    color: "#475569",
    cursor: "pointer",
  };

  return (
    <div className="upload-card rounded-2xl border border-border bg-surface p-8 shadow-sm">
      <h2 className="mb-1 text-lg font-semibold text-slate-900">Choose a template</h2>
      <p className="mb-6 text-sm text-slate-500">
        Select a starting design for your storefront. You can adjust colors and fonts on the next step.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 16 }}>
        {TEMPLATES.map((t) => (
          <TemplateCard
            key={t.id}
            template={t}
            selected={selectedId === t.id}
            onSelect={() => {
              setSelectedId(t.id);
              if (t.id !== "sport-retail") setIsRental(false);
            }}
            onPreview={() => setPreviewId(t.id)}
          />
        ))}
      </div>

      {/* Rental sub-option — only visible when Retail template is selected */}
      {selectedId === "sport-retail" && (
        <div
          style={{
            marginTop: 20,
            padding: "16px 20px",
            borderRadius: 12,
            border: isRental ? "2px solid #2563eb" : "1px solid #e2e8f0",
            background: isRental ? "#eff6ff" : "#f8fafc",
            transition: "border-color 0.15s, background 0.15s",
          }}
        >
          <label style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={isRental}
              onChange={(e) => setIsRental(e.target.checked)}
              style={{ marginTop: 3, width: 16, height: 16, accentColor: "#2563eb", flexShrink: 0 }}
            />
            <div>
              <p style={{ fontWeight: 600, fontSize: 14, color: "#1e293b", margin: 0 }}>
                Rental Services
              </p>
              <p style={{ fontSize: 13, color: "#64748b", margin: "4px 0 0" }}>
                Enable date-based inventory management. Customers will select rental start &amp; end dates and the system will enforce availability limits per item.{" "}
                <strong style={{ color: "#dc2626" }}>Requires an inventory column in your CSV.</strong>
              </p>
            </div>
          </label>
        </div>
      )}

      <div className="mt-8 flex gap-3">
        {onBack && (
          <button type="button" onClick={onBack} style={btnSecondary}>Back</button>
        )}
        <button type="button" onClick={handleNext} style={btnPrimary} disabled={!selectedId}>
          Next: upload catalog
        </button>
      </div>

      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewId(null)}
          onUse={() => {
            setSelectedId(previewTemplate.id);
            setPreviewId(null);
          }}
        />
      )}
    </div>
  );
}
